import {
  CompaniesInfiniteSelectField,
  DeleteIcon,
  FloatingFormItem,
  Group,
  InputDate,
  LicensePlateFormField,
} from '@/components/_common';
import BrandsInfiniteSelect from '@/components/_common/BrandsInfiniteSelect';
import BrandsModelsInfiniteSelect from '@/components/_common/BrandsModelsInfiniteSelect';
import CustomersSelectField from '@/components/_common/CustomersSelectField';
import SelectPartsListModal from '@/components/_common/SelectPartsListModal';
import SelectTasksListModal from '@/components/_common/SelectTasksListModal';
import { Task } from '@/core/domain/operational/Tasks/Tasks.types';
import { useReportsGenerateCrmNextRevision } from '@/core/domain/reports/generate/reports-generate.hooks';
import { ReportsGenerateCrmNextRevisionRequest } from '@/core/domain/reports/generate/reports-generate.types';
import { StockItem } from '@/core/domain/stock/StockItems/StockItems.types';
import { useDisclosure, useReportGenerate } from '@/hooks';
import {
  Modal,
  Form,
  Row,
  Col,
  Radio,
  Card,
  Table,
  Button,
  message,
  Image,
} from 'antd';
import { AxiosError } from 'axios';
import moment from 'moment';
import { Moment } from 'moment';
import { useCallback } from 'react';

interface Props {
  opened: boolean;
  onClose: VoidFunction;
}

interface FormValues {
  initialDate: Moment;
  finalDate: Moment;
  companiesIds?: number[];
  customerIds?: number[];
  modelIds?: number[];
  brandIds?: number[];
  licensePlate?: string;
  itemType?: 'PART' | 'TASK';
  parts: StockItem[];
  tasks: Task[];
}

export default function CrmReportNextRevisionModal(props: Props) {
  const [form] = Form.useForm<FormValues>();
  const itemType = Form.useWatch('itemType', form);
  const initialDate = Form.useWatch('initialDate', form);
  const finalDate = Form.useWatch('finalDate', form);
  const brandIds = Form.useWatch('brandIds', form);
  const parts = Form.useWatch('parts', form) || [];
  const tasks = Form.useWatch('tasks', form) || [];

  const [partsOpened, partsAction] = useDisclosure();
  const [tasksOpened, tasksAction] = useDisclosure();

  const report = useReportGenerate();

  const generateCrmReportNextRevision = useReportsGenerateCrmNextRevision();

  const handleClose = () => {
    form.resetFields();
    generateCrmReportNextRevision.reset();
    props.onClose();
  };

  const handleSubmit = async () => {
    try {
      const { parts, ...values } = await form.validateFields();

      const payload: ReportsGenerateCrmNextRevisionRequest = {
        ...values,
        partIds:
          values.itemType === 'PART' && parts.length
            ? parts.map((part) => part.partId)
            : undefined,
        taskIds:
          values.itemType === 'TASK' && tasks.length
            ? tasks.map((task) => task.id)
            : undefined,
        initialDate: values.initialDate.format(),
        finalDate: values.finalDate.format(),
      };

      const result = await generateCrmReportNextRevision.mutateAsync(payload);

      if (result) {
        report.downloadFile({
          result,
          fileName: `relatorio-proximas-revisoes_${moment().format(
            'DD-MM-YYYY_HH-mm-ss',
          )}.pdf`,
        });
      }

      handleClose();
    } catch (error: unknown) {
      if (!(error as AxiosError).isAxiosError) {
        message.error('Verifique os campos do formulário');
      }
    }
  };

  const removePart = useCallback(
    (partId: number) => {
      form.setFieldValue(
        'parts',
        parts.filter((part) => part.partId !== partId),
      );
    },
    [parts],
  );

  const removeTask = useCallback(
    (taskId: number) => {
      form.setFieldValue(
        'tasks',
        tasks.filter((part) => part.id !== taskId),
      );
    },
    [tasks],
  );

  return (
    <>
      <Modal
        title="Relatório de próximas revisões"
        visible={props.opened}
        onCancel={handleClose}
        width={640}
        okText="Baixar PDF"
        onOk={handleSubmit}
        confirmLoading={generateCrmReportNextRevision.isLoading}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ itemType: '', parts: [], tasks: [] }}
        >
          <Form.Item name="parts" initialValue={[]} hidden />
          <Form.Item name="tasks" initialValue={[]} hidden />

          <Row gutter={[16, 16]}>
            <Col span={24} sm={12}>
              <FloatingFormItem
                name="initialDate"
                label="Data inicial"
                rules={[{ required: true, message: 'Informe a data inicial' }]}
              >
                <InputDate disabledDate={(date) => date.isAfter(finalDate)} />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12}>
              <FloatingFormItem
                name="finalDate"
                label="Data final"
                rules={[{ required: true, message: 'Informe a data final' }]}
              >
                <InputDate
                  disabledDate={(date) => date.isBefore(initialDate)}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24}>
              <FloatingFormItem name="companiesIds" label="Empresas">
                <CompaniesInfiniteSelectField
                  buttonSelectAll
                  mode="multiple"
                  allowClear
                />
              </FloatingFormItem>
            </Col>

            <Col span={24}>
              <FloatingFormItem name="customerIds" label="Clientes">
                <CustomersSelectField mode="multiple" allowClear />
              </FloatingFormItem>
            </Col>

            <Col span={24}>
              <LicensePlateFormField
                name="licensePlate"
                label="Placa"
                required={false}
              />
            </Col>

            <Col span={12}>
              <FloatingFormItem name="brandIds" label="Marcas">
                <BrandsInfiniteSelect mode="multiple" allowClear />
              </FloatingFormItem>
            </Col>

            <Col span={12}>
              <FloatingFormItem name="modelIds" label="Modelos">
                <BrandsModelsInfiniteSelect
                  mode="multiple"
                  allowClear
                  queryFilter={{ ids: brandIds }}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24}>
              <Form.Item
                name="itemType"
                label="Tipo de item"
                style={{ margin: 0 }}
                getValueFromEvent={(ev) => {
                  form.setFieldValue('parts', []);
                  form.setFieldValue('tasks', []);
                  return ev.target.value;
                }}
              >
                <Radio.Group optionType="button" buttonStyle="solid">
                  <Radio.Button value="">Ambos</Radio.Button>
                  <Radio.Button value="PART">Peça</Radio.Button>
                  <Radio.Button value="TASK">Serviço</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>

            {itemType === 'PART' && (
              <Col span={24}>
                <Card
                  style={{
                    overflow: 'hidden',
                    borderRadius: 8,
                  }}
                  bodyStyle={{
                    overflowX: 'auto',
                    maxHeight: 300,
                    paddingInline: 6,
                    paddingBlock: 16,
                  }}
                  headStyle={{ paddingInline: 12, paddingBlock: 0 }}
                  title={
                    <Group gap={16} position="between">
                      <span>Peças selecionadas</span>

                      <Group gap={16}>
                        <Button onClick={partsAction.open}>Selecionar</Button>
                        {!!parts.length && (
                          <Button
                            onClick={() => form.setFieldValue('parts', [])}
                            type="ghost"
                            danger
                          >
                            Remover todos
                          </Button>
                        )}
                      </Group>
                    </Group>
                  }
                >
                  <Table
                    pagination={false}
                    dataSource={parts}
                    columns={[
                      {
                        width: 50,
                        render: (_, part) => (
                          <Image
                            src={part.imageUrl}
                            width={40}
                            height={40}
                            style={{
                              border: '1px solid lightgray',
                              objectFit: 'cover',
                            }}
                          />
                        ),
                      },
                      { title: 'Descrição', dataIndex: 'description' },
                      { title: 'Cód. Interno', dataIndex: 'sku' },
                      {
                        title: 'Ação',
                        align: 'right',
                        render: (_, part) => (
                          <Group position="right">
                            <DeleteIcon
                              onClick={() => removePart(part.partId)}
                            />
                          </Group>
                        ),
                      },
                    ]}
                  />
                </Card>
              </Col>
            )}

            {itemType === 'TASK' && (
              <Col span={24}>
                <Card
                  style={{
                    overflow: 'hidden',
                    borderRadius: 8,
                  }}
                  bodyStyle={{
                    overflowX: 'auto',
                    maxHeight: 300,
                    paddingInline: 6,
                    paddingBlock: 16,
                  }}
                  headStyle={{ paddingInline: 12, paddingBlock: 0 }}
                  title={
                    <Group gap={16} position="between">
                      <span>Serviços selecionadas</span>

                      <Group gap={16}>
                        <Button onClick={tasksAction.open}>Selecionar</Button>
                        {!!tasks.length && (
                          <Button
                            onClick={() => form.setFieldValue('tasks', [])}
                            type="ghost"
                            danger
                          >
                            Remover todos
                          </Button>
                        )}
                      </Group>
                    </Group>
                  }
                >
                  <Table
                    pagination={false}
                    dataSource={tasks}
                    columns={[
                      { title: 'Descrição', dataIndex: 'description' },
                      { title: 'Tempário', dataIndex: 'temparioCode' },
                      {
                        title: 'Ação',
                        align: 'right',
                        render: (_, task) => (
                          <Group position="right">
                            <DeleteIcon onClick={() => removeTask(task.id)} />
                          </Group>
                        ),
                      },
                    ]}
                  />
                </Card>
              </Col>
            )}
          </Row>
        </Form>
      </Modal>

      <SelectPartsListModal
        itemSelectedIds={parts.map((part) => part.partId)}
        onCancel={partsAction.close}
        open={partsOpened}
        key={Number(partsOpened)}
        onCallback={(parts) => form.setFieldValue('parts', parts)}
      />

      <SelectTasksListModal
        itemSelectedIds={tasks.map((task) => task.id)}
        onCancel={tasksAction.close}
        open={tasksOpened}
        key={Number(tasksOpened)}
        onCallback={(tasks) => form.setFieldValue('tasks', tasks)}
        multiple
      />
    </>
  );
}
